import type { Building } from "building-types/building/buildingTypes";
import { tagBuildingFaces } from "building-types/buildingGeomTag/buildingGeomTags";
import type { BuildingGeomTag } from "building-types/buildingGeomTag/buildingGeomTagTypes";
import type { GeneratedDoorPlacement } from "building-types/doors/doorTypes";
import type { Level } from "building-types/levels/levelTypes";
import { type OpeningSide } from "building-types/openings/openingTypes";
import type {
  HalfEdgeAssociationTable,
  LevelHalfEdgeAssociationTable,
  RWLevelHalfEdgeAssociationTable,
} from "building-types/planarGraphs/planarGraphAssociationTypes";
import { WindowPanelType } from "building-types/settings/windowTypesEnum";
import type { WallCut } from "building-types/walls/wallCutTypes";
import { WallType } from "building-types/walls/wallEnums";
import type { WallFaces } from "building-types/walls/wallFacesTypes";
import type { WallMassSystem } from "building-types/walls/wallSytemTypes";
import type { GeneratedWindowPlacement } from "building-types/windows/windowTypes";
import type { Table } from "common/data/table";
import { doorPlacementsOnLevel } from "doc/building/doorsOnLevel";
import { windowPlacementsOnLevel } from "doc/building/windowPlacementsOnLevel";
import { aabb2ContainsAABB2, aabb2Grow, aabb2Overlaps } from "geom/aabb2";
import type { AABB2 } from "geom/aabb2Types";
import type { Face } from "geom/brep";
import { faceNew } from "geom/brep";
import { buildBSPNode, buildEdgePolygon } from "geom/bsp";
import type { ClipFaceContext } from "geom/bspClipping";
import { clipFaces, clipFacesWithMultipleContexts, clippingNodeNew } from "geom/bspClipping";
import type { PolygonBSP } from "geom/bspTypes";
import { BSP_EPSILON } from "geom/bspTypes";
import { closestParamLine2 } from "geom/closestPoint";
import { DIST_EPSILON, WindingDir } from "geom/core";
import { CurveCurveRangeIntersection, intersectLine2Line2 } from "geom/intersectLine2Line2";
import { line2Eval } from "geom/line2";
import { line3Tangent } from "geom/line3";
import { planarGraphAdjacentHalfEdge, planarGraphHalfEdgeEnd, planarGraphHalfEdgeStart } from "geom/planarGraph";
import {
  planeCopy,
  planeEval,
  planeMoveAlongNormal,
  planeNormal,
  planeProjectPolyLine3,
  planeReProjectPolyLine2,
} from "geom/plane";
import type { Plane } from "geom/planeTypes";
import {
  polyLine2AABB2,
  polyLine2EdgeEval,
  polyLine2EdgeLength,
  polyLine2EdgeTangent,
  polyLine2IsAxisAlignedRect,
  polyLine2IsEqual,
  polyLine2Length,
  polyLine2MoveCopy,
  polyLine2WindingDir,
} from "geom/polyLine2";
import { polyLine2New, type PolyLine2 } from "geom/polyLine2Types";
import type { PolyLine3 } from "geom/polyLine3";
import {
  polyLine3AABB3,
  polyLine3EdgeCount,
  polyLine3EdgeEnd,
  polyLine3EdgeStart,
  polyLine3OnPlane,
  polyLine3ReverseCopy,
} from "geom/polyLine3";
import { ray2DotProject } from "geom/ray2";
import { vec2Dist } from "geom/vec2";
import { getFinalDoorPosition } from "../doors/doorUtils";
import { makeOpeningSide } from "../openings/openingProfile";
import { makeWallCutOutline, wallCutNew } from "../wallCuts";
import { windowRectangularOpening } from "../window/windowElevation";
import { getWindowHeight } from "../window/windowHeightTools";

export interface HalfEdgeToWindowSet {
  [halfEdge: number]: GeneratedWindowPlacement[];
}

export interface HalfEdgeToDoorSet {
  [halfEdge: number]: GeneratedDoorPlacement[];
}

export interface HalfEdgeToWallCutSet {
  [halfEdge: number]: WallCut[];
}

export function makeWallCutHalfEdgeMaps(
  doors: GeneratedDoorPlacement[],
  windows: GeneratedWindowPlacement[],
  wallCutTable: HalfEdgeAssociationTable<WallCut>,
): [HalfEdgeToWindowSet, HalfEdgeToDoorSet, HalfEdgeToWallCutSet] {
  const halfEdgeToWindowSet: HalfEdgeToWindowSet = {};
  const halfEdgeToDoorSet: HalfEdgeToDoorSet = {};
  const halfEdgeToWallCutSet: HalfEdgeToWallCutSet = {};

  function addDoorToHalfEdge(halfEdgeIndex: number, door: GeneratedDoorPlacement) {
    if (!halfEdgeToDoorSet[halfEdgeIndex]) {
      halfEdgeToDoorSet[halfEdgeIndex] = [];
    }

    halfEdgeToDoorSet[halfEdgeIndex].push(door);

    if (!(door.descriptor.id === "OPEN_FULL" ||
        door.descriptor.id === "OPEN_ARC_FULL" ||
        door.descriptor.width > DIST_EPSILON)) {
      console.assert(false, "illegal door width");
    }
    if (!(door.descriptor.id === "OPEN_FULL" ||
        door.descriptor.id === "OPEN_ARC_FULL" ||
        door.descriptor.height > DIST_EPSILON)) {
      console.assert(false, "illegal door height");
    }
  }

  for (let i = 0, l = doors.length; i < l; i++) {
    const door = doors[i];
    if (!(!door.descriptor.requiresHeightComputation)) {
      console.assert(false, "Door without height computed not allowed");
    }
    addDoorToHalfEdge(door.halfEdgeIndex, door);
  }

  for (let i = 0, l = windows.length; i < l; i++) {
    const window = windows[i];
    const halfEdge = window.halfEdgeIndex;

    if (!halfEdgeToWindowSet[halfEdge]) {
      halfEdgeToWindowSet[halfEdge] = [];
    }

    halfEdgeToWindowSet[halfEdge].push(window);

    if (!(window.descriptor.width > DIST_EPSILON)) {
      console.assert(false);
    }
    if (!(getWindowHeight(window.descriptor) > DIST_EPSILON)) {
      console.assert(false);
    }
  }

  if (wallCutTable !== undefined) {
    for (const halfEdgeIndex in wallCutTable) {
      const wallCuts = wallCutTable[halfEdgeIndex];
      if (!wallCuts) {
        continue;
      }
      for (const wallCut of wallCuts) {
        const { item } = wallCut;
        item;
        !halfEdgeToWallCutSet[halfEdgeIndex]
          ? (halfEdgeToWallCutSet[halfEdgeIndex] = [item])
          : halfEdgeToWallCutSet[halfEdgeIndex].push(item);
      }
    }
  }

  return [halfEdgeToWindowSet, halfEdgeToDoorSet, halfEdgeToWallCutSet];
}

export function getHoleFaces(hole: PolyLine2, plane: Plane, wallThickness: number): Face[] {
  let [vX, vY, vZ] = planeNormal(plane);
  vX = -vX;
  vY = -vY;
  vZ = -vZ;

  const faces: Face[] = [];

  for (let i = 0, l = hole.length - 2; i < l; i += 2) {
    const au = hole[i];
    const av = hole[i + 1];

    const bu = hole[i + 2];
    const bv = hole[i + 3];

    const u = vec2Dist(au, av, bu, bv);
    const v = wallThickness;

    const [originX, originY, originZ] = planeEval(plane, au, av);
    const [endX, endY, endZ] = planeEval(plane, bu, bv);
    const [uX, uY, uZ] = line3Tangent(originX, originY, originZ, endX, endY, endZ);

    const facePlane: Plane = {
      originX,
      originY,
      originZ,
      uX,
      uY,
      uZ,
      vX,
      vY,
      vZ,
    };

    const faceLoop = polyLine2New([0, 0, 0, v, u, v, u, 0, 0, 0]);

    faces.push(faceNew([faceLoop], facePlane));
  }

  return faces;
}

export function getBackSide({
  hole,
  frontEdge,
  backEdge,
  ignoreLimits = false,
}: {
  hole: OpeningSide;
  frontEdge: PolyLine2;
  backEdge: PolyLine2;
  ignoreLimits: boolean;
}): OpeningSide | null {
  const { opening2: loop, openingCasing2: loopWithCasing } = hole;
  const projectedLoop = projectLoop(loop, frontEdge, backEdge);

  const startParam = projectedLoop[0];
  const endParam = projectedLoop[2];

  const backLen = polyLine2EdgeLength(backEdge, 0);

  if (!ignoreLimits && (startParam < 0 || startParam > backLen || endParam < 0 || endParam > backLen)) {
    return null;
  }

  if (!loopWithCasing) {
    return makeOpeningSide({ ...hole, opening2: projectedLoop, startParam, endParam }) as OpeningSide;
  }

  return makeOpeningSide({
    ...hole,
    opening2: projectedLoop,
    openingCasing2: projectLoop(loopWithCasing, frontEdge, backEdge),
    startParam,
    endParam,
  }) as OpeningSide;
}

function projectLoop(loop: PolyLine2, frontEdge: PolyLine2, backEdge: PolyLine2): PolyLine2 {
  // assumes rectangular window
  const rightFrontParam = loop[2];

  // compute the position of the extrema of the window on the front edge
  const [rightX, rightY] = polyLine2EdgeEval(frontEdge, 0, rightFrontParam, false);

  // construct rays running perpendicular to the front edge, through the extrema on the front edge
  // and intersect it with the back edge

  const t = closestParamLine2(backEdge[0], backEdge[1], backEdge[2], backEdge[3], rightX, rightY, false);

  return polyLine2MoveCopy(loop, t - loop[0], 0);
}

export function clipWallFaces(wallFaces: WallFaces, options: ClipFaceContext, onlyLeftFaces: boolean = false) {
  const clipPred = (faces: Face[]): Face[] => {
    return clipFaces({
      faces,
      options,
    });
  };

  operateWallFaces(wallFaces, clipPred, onlyLeftFaces);
}

export function clipWallFacesWithMultipleContexts(
  wallFaces: WallFaces,
  optionList: ClipFaceContext[],
  onlyLeftFaces: boolean = false,
) {
  const clipPred = (faces: Face[]): Face[] => {
    return clipFacesWithMultipleContexts({
      faces,
      optionList,
    });
  };

  operateWallFaces(wallFaces, clipPred, onlyLeftFaces);
}

const addTagOperateWallFace: BuildingGeomTag = {
  type: "OPERATION",
  operation: "OPERATE_WALL_FACE",
};

export function operateWallFaces(
  wallFaces: WallFaces,
  operationPred: (faces: Face[]) => Face[],
  onlyLeftFaces: boolean,
) {
  wallFaces.leftFaces = tagBuildingFaces(operationPred(wallFaces.leftFaces), addTagOperateWallFace);
  if (!onlyLeftFaces) {
    wallFaces.rightFaces = tagBuildingFaces(operationPred(wallFaces.rightFaces), addTagOperateWallFace);
    wallFaces.leftHoleFaces = tagBuildingFaces(operationPred(wallFaces.leftHoleFaces), addTagOperateWallFace);
    wallFaces.rightHoleFaces = tagBuildingFaces(operationPred(wallFaces.rightHoleFaces), addTagOperateWallFace);
    wallFaces.startFaces = tagBuildingFaces(operationPred(wallFaces.startFaces), addTagOperateWallFace);
    wallFaces.endFaces = tagBuildingFaces(operationPred(wallFaces.endFaces), addTagOperateWallFace);
    wallFaces.topFaces = tagBuildingFaces(operationPred(wallFaces.topFaces), addTagOperateWallFace);
    wallFaces.bottomFaces = tagBuildingFaces(operationPred(wallFaces.bottomFaces), addTagOperateWallFace);
    wallFaces.leftExteriorFaces = tagBuildingFaces(operationPred(wallFaces.leftExteriorFaces), addTagOperateWallFace);
    wallFaces.rightExteriorFaces = tagBuildingFaces(operationPred(wallFaces.rightExteriorFaces), addTagOperateWallFace);
  }
}

export function performWallCut(wallFaces: WallFaces, cutFace: Face) {
  const cutPlane = cutFace.plane;
  const cutLoop = cutFace.loops[0];
  const cutLoop3 = polyLine3OnPlane(cutLoop, cutFace.plane);

  const { leftFaces, rightFaces } = wallFaces;

  const leftResult = insertLoopIntoWallFacesFast(leftFaces, cutLoop, cutPlane);
  const rightResult = insertLoopIntoWallFacesFast(rightFaces, cutLoop, cutPlane);

  if (leftResult && rightResult) {
    return;
  }

  // perform the cut with a slow bsp path
  performWallCut3(wallFaces, cutFace.plane, cutLoop3);
}

/**
 * Detect if there is any overlap with existing inner loops. Windows are very commonly
 * disjoint rectangles, so this is very common.
 */
function validCutForFastPath(loops: PolyLine2[], cutLoopAABB: AABB2): boolean {
  const loopsCount = loops.length;

  if (loops.length < 2) {
    return true;
  }

  for (let i = 1; i < loopsCount; i++) {
    if (aabb2Overlaps(polyLine2AABB2(loops[i]), cutLoopAABB)) {
      return false;
    }
  }

  return true;
}

/**
 * For some reason, probably a bug, we can end up with two loops that are identical.
 */
function isDuplicateLoop(loops: PolyLine2[], cutLoopAABB: AABB2, loop: PolyLine2): boolean {
  const loopsCount = loops.length;

  for (let i = 1; i < loopsCount; i++) {
    if (aabb2Overlaps(polyLine2AABB2(loops[i]), cutLoopAABB) && polyLine2IsEqual(loops[i], loop)) {
      return true;
    }
  }

  return false;
}

function insertLoopIntoWallFacesFast(wallFaces: Face[], cutLoop: PolyLine2, cutPlane: Plane): boolean {
  if (wallFaces.length === 0) {
    return true;
  }

  if (wallFaces.length > 1) {
    return false;
  }

  const face = wallFaces[0];

  const { loops, plane } = face;

  // The fast path only works for rectangular faces, which is by far the most common case.
  if (!polyLine2IsAxisAlignedRect(loops[0])) {
    return false;
  }

  if (!polyLine2IsAxisAlignedRect(cutLoop)) {
    return false;
  }

  const projectedCutLoop = planeReProjectPolyLine2(cutLoop, cutPlane, plane);

  // We detect if the loop is disjoint and inside of the wall.

  const faceBB = aabb2Grow(polyLine2AABB2(loops[0]), -DIST_EPSILON, -DIST_EPSILON);
  const cutBB = polyLine2AABB2(projectedCutLoop);

  if (!aabb2ContainsAABB2(faceBB, cutBB)) {
    return false;
  }

  if (isDuplicateLoop(loops, cutBB, projectedCutLoop)) {
    return true;
  }

  if (validCutForFastPath(loops, cutBB)) {
    loops.push(projectedCutLoop);
    return true;
  }

  return false;
}

export function performWallCut3(wallFaces: WallFaces, cutPlane: Plane, cutLoop: PolyLine3) {
  // build the hole perimeter edge polygons
  const [nX, nY, nZ] = planeNormal(cutPlane);
  const polygons: PolygonBSP[] = [];
  const cutLoopCount = cutLoop ? polyLine3EdgeCount(cutLoop) : 0;
  const reversed = polyLine3ReverseCopy(cutLoop);
  for (let i = 0; i < cutLoopCount; i++) {
    const [x0, y0, z0] = polyLine3EdgeStart(reversed, i);
    const [x1, y1, z1] = polyLine3EdgeEnd(reversed, i);

    const edgePoly = buildEdgePolygon(x0, y0, z0, x1, y1, z1, nX, nY, nZ);
    if (edgePoly) {
      // buildEdgePolygon can return null if the
      // loop is degenerate
      polygons.push(edgePoly);
    }
  }

  // build the BSP from this polygons
  const clipBSP = buildBSPNode({ polygons });

  // check if we need to revert the clipping
  const loop = planeProjectPolyLine3(cutPlane, reversed);
  const revert = polyLine2WindingDir(loop) === WindingDir.CW;

  // clip the wall faces
  const options: ClipFaceContext = {
    clippingNodes: [clippingNodeNew(clipBSP, revert)],
  };

  clipWallFaces(wallFaces, options, false);
}

export function performWallCutOnLeftOrRightFace(
  wallFaces: WallFaces,
  cutPlane: Plane,
  cutLoop: PolyLine2,
  isLeftCut: boolean,
) {
  // convert to PolyLine3
  const cutLoop3 = polyLine3OnPlane(cutLoop, cutPlane);

  // perform the cut
  performWallCut3OnRightOrLeftFaces(wallFaces, cutPlane, cutLoop3, isLeftCut);
}

const addTagWallCut30nFace: BuildingGeomTag = {
  type: "OPERATION",
  operation: "WALL_CUT_30n_FACE",
};

function performWallCut3OnRightOrLeftFaces(
  wallFaces: WallFaces,
  cutPlane: Plane,
  cutLoop: PolyLine3,
  isLeftCut: boolean,
) {
  // build the hole perimeter edge polygons
  const [nX, nY, nZ] = planeNormal(cutPlane);
  const polygons: PolygonBSP[] = [];
  const cutLoopCount = cutLoop ? polyLine3EdgeCount(cutLoop) : 0;
  for (let i = 0; i < cutLoopCount; i++) {
    const [x0, y0, z0] = polyLine3EdgeStart(cutLoop, i);
    const [x1, y1, z1] = polyLine3EdgeEnd(cutLoop, i);

    const edgePoly = buildEdgePolygon(x0, y0, z0, x1, y1, z1, nX, nY, nZ);
    if (edgePoly) {
      // buildEdgePolygon can return null if the
      // loop is degenerate
      polygons.push(edgePoly);
    }
  }

  // build the BSP from this polygons
  const clipBSP = buildBSPNode({ polygons });

  // clip the wall faces
  const options: ClipFaceContext = {
    clippingNodes: [clippingNodeNew(clipBSP)],
  };

  if (isLeftCut) {
    wallFaces.leftFaces = tagBuildingFaces(
      clipFaces({
        faces: wallFaces.leftFaces,
        options,
      }),
      addTagWallCut30nFace,
    );
  } else {
    wallFaces.rightFaces = tagBuildingFaces(
      clipFaces({
        faces: wallFaces.rightFaces,
        options,
      }),
      addTagWallCut30nFace,
    );
  }
}

interface TryToAddExtraWallCutFromHoleOptions {
  halfEdgeIndex: number;
  frontHole: OpeningSide;
  frontPlane: Plane;
  frontOffset: number;
  levels: Table<Level>;
  levelId: string;
  wallMasses: Table<WallMassSystem>;
  allWallCuts: RWLevelHalfEdgeAssociationTable<WallCut>;
}

function tryToAddExtraWallCutFromHole({
  halfEdgeIndex,
  frontHole,
  frontPlane,
  frontOffset,
  levels,
  levelId,
  wallMasses,
  allWallCuts,
}: TryToAddExtraWallCutFromHoleOptions) {
  const frontLoop3 = polyLine3OnPlane(frontHole.opening2, planeMoveAlongNormal(planeCopy(frontPlane), -frontOffset));
  const { minZ: bottom, maxZ: top } = polyLine3AABB3(frontLoop3);

  const startX = frontLoop3[0];
  const startY = frontLoop3[1];

  const endX = frontLoop3[3];
  const endY = frontLoop3[4];

  // look for extra wall cuts in upper levels
  tryToAddExtraWallCut({
    levels,
    levelId: levels[levelId].levelAbove,
    prevLevelId: levelId,
    levelInc: "levelAbove",
    wallMasses,
    halfEdgeIndex,
    bottom,
    top,
    startX,
    startY,
    endX,
    endY,
    allWallCuts,
  });

  // look for extra wall cuts in lower levels
  tryToAddExtraWallCut({
    levels,
    levelId: levels[levelId].levelBelow,
    prevLevelId: levelId,
    levelInc: "levelBelow",
    wallMasses,
    halfEdgeIndex,
    bottom,
    top,
    startX,
    startY,
    endX,
    endY,
    allWallCuts,
  });

  // look for extra wall cuts in the same level
  tryToAddExtraWallCut({
    levels,
    levelId,
    prevLevelId: levelId,
    wallMasses,
    halfEdgeIndex,
    bottom,
    top,
    startX,
    startY,
    endX,
    endY,
    allWallCuts,
  });
}

function addHalfEdgeToWallCutSet(
  wallCutSets: RWLevelHalfEdgeAssociationTable<WallCut>,
  levelId: string,
  halfEdgeIndex: string,
  wallCut: WallCut,
) {
  if (!wallCutSets[levelId]) {
    wallCutSets[levelId] = {};
  }

  const levelWallCuts = wallCutSets[levelId];

  if (!levelWallCuts[halfEdgeIndex]) {
    levelWallCuts[halfEdgeIndex] = [];
  }

  levelWallCuts[halfEdgeIndex].push({ item: wallCut });
}

interface TryToAddExtraWallCutOptions {
  levels: Table<Level>;
  levelId: string | undefined;
  prevLevelId: string;
  wallMasses: Table<WallMassSystem>;
  halfEdgeIndex: number;
  bottom: number;
  top: number;
  startX: number;
  startY: number;
  endX: number;
  endY: number;
  allWallCuts: RWLevelHalfEdgeAssociationTable<WallCut>;
  levelInc?: keyof Pick<Level, "levelAbove" | "levelBelow">; // how to increment
  visitedLevelIds?: Set<string>; // prevent infinite recursion from circular level relationships
}

function tryToAddExtraWallCut({
  levels,
  levelId,
  prevLevelId,
  levelInc,
  wallMasses,
  halfEdgeIndex,
  bottom,
  top,
  startX,
  startY,
  endX,
  endY,
  allWallCuts,
  visitedLevelIds = new Set<string>(),
}: TryToAddExtraWallCutOptions) {
  if (!(!!levels[prevLevelId])) {
    console.assert(false);
  }

  // is a valid level
  if (!levelId || !levels[levelId]) {
    return;
  }

  // Prevent infinite recursion from circular level relationships
  if (visitedLevelIds.has(levelId)) {
    return;
  }
  visitedLevelIds.add(levelId);

  const level = levels[levelId];
  const { floorElevation, topOfWall } = level;
  const { halfEdgeToWall, walls } = wallMasses[levelId];
  const ceilingElevation = floorElevation + topOfWall;

  // check vertical overlap
  if (top <= floorElevation || bottom >= ceilingElevation) {
    return;
  }

  const planarGraph = wallMasses[levelId].wallIndex.planarGraph.planarGraph;
  const { halfEdges } = planarGraph;
  const [centerX, centerY] = line2Eval(startX, startY, endX, endY, 0.5, true);

  const visitedHalfEdges: { [halfEdge: number]: boolean } = {};

  // look for coincident edges on this level
  for (let i = 0, l = halfEdges.length / 2; i < l; i++) {
    if (visitedHalfEdges[i]) {
      continue;
    }

    const adjI = planarGraphAdjacentHalfEdge(i);

    if (!levelInc && (i === halfEdgeIndex || adjI === halfEdgeIndex)) {
      continue;
    }

    visitedHalfEdges[i] = true;
    visitedHalfEdges[adjI] = true;

    const [x0, y0] = planarGraphHalfEdgeStart(planarGraph, i);
    const [x1, y1] = planarGraphHalfEdgeEnd(planarGraph, i);

    // check for edge overlap
    const intersection = intersectLine2Line2(startX, startY, endX, endY, x0, y0, x1, y1, BSP_EPSILON);
    if (intersection && intersection instanceof CurveCurveRangeIntersection) {
      const wallCutBottom = bottom - floorElevation;
      const wallCutTop = top - floorElevation;

      const wallId = halfEdgeToWall[i];
      if (!wallId) {
        continue;
      }

      const wall = walls[wallId];
      if (!wall || wall.wallType !== WallType.SOLID_MASS) {
        continue;
      }

      // reproject the hole
      const curFrontEdge = wall.leftEdge;
      const [dirX, dirY] = polyLine2EdgeTangent(curFrontEdge, 0);
      const t = ray2DotProject(curFrontEdge[0], curFrontEdge[1], dirX, dirY, centerX, centerY);

      // add the extra wall cut
      const wallCut = wallCutNew({
        edgeParam: t,
        edgeParamNormalized: false,
        edgeParamAbsolute: true,
        width: vec2Dist(startX, startY, endX, endY),
        height: wallCutTop - wallCutBottom,
        bottom: wallCutBottom,
        opening: undefined, // setting undefined to indicate this has not been set
      });

      addHalfEdgeToWallCutSet(allWallCuts, levelId, i.toString(), wallCut);
    }
  }

  // move to the next level
  if (levelInc && level[levelInc]) {
    tryToAddExtraWallCut({
      levels,
      levelId: level[levelInc],
      prevLevelId: levelId,
      levelInc,
      wallMasses,
      halfEdgeIndex,
      bottom,
      top,
      startX,
      startY,
      endX,
      endY,
      allWallCuts,
      visitedLevelIds,
    });
  }
}

interface CollectWallCutsOptions {
  levels: Table<Level>;
  doors: Table<GeneratedDoorPlacement>;
  windows: Table<GeneratedWindowPlacement>;
  wallCuts: Building["wallCuts"];
  wallMasses: Table<WallMassSystem>;
}

export function collectWallCuts({
  levels,
  doors,
  windows,
  wallMasses,
  wallCuts,
}: CollectWallCutsOptions): LevelHalfEdgeAssociationTable<WallCut> {
  const allWallCuts: RWLevelHalfEdgeAssociationTable<WallCut> = {};

  // iterate all levels
  for (const levelId in levels) {
    const levelDoors = doorPlacementsOnLevel(levelId, doors);
    const levelWindows = windowPlacementsOnLevel(levelId, windows);
    const wallCutTable = wallCuts[levelId];
    const wallMassSystem = wallMasses[levelId];
    const { halfEdgeToWall, walls } = wallMassSystem;

    // iterate level windows
    for (const window of levelWindows) {
      const { halfEdgeIndex } = window;

      const panelType = window?.materials?.panelType;
      if (panelType === WindowPanelType.FAUX_SHUTTER) {
        continue;
      }

      const wallId = halfEdgeToWall[halfEdgeIndex];

      if (!wallId) {
        continue;
      }

      const wall = walls[wallId];

      if (!wall || wall.wallType !== WallType.SOLID_MASS) {
        continue;
      }

      const isLeftWindow = wall.path.halfEdgeIndex === halfEdgeIndex;
      const { leftEdge, rightEdge, leftOffset, rightOffset, leftFace, rightFace } = wall;
      const frontEdge = isLeftWindow ? leftEdge : rightEdge;
      const frontFace = isLeftWindow ? leftFace : rightFace;
      const frontOffset = isLeftWindow ? leftOffset : rightOffset;
      const frontEdgeLen = polyLine2Length(frontEdge);
      const frontHole = windowRectangularOpening(window, frontEdge, frontEdgeLen, wallMassSystem);

      if (!frontHole) {
        continue;
      }

      // look for extra wall cuts for this window
      tryToAddExtraWallCutFromHole({
        halfEdgeIndex,
        frontHole,
        frontPlane: frontFace.plane,
        frontOffset,
        levels,
        levelId,
        wallMasses,
        allWallCuts,
      });
    }

    // iterate level doors
    for (const door of levelDoors) {
      const halfEdgeIndex = door.halfEdgeIndex;
      const wallId = halfEdgeToWall[halfEdgeIndex];

      if (!wallId) {
        continue;
      }

      const wall = walls[wallId];

      if (!wall || wall.wallType !== WallType.SOLID_MASS) {
        continue;
      }

      const isLeftWindow = wall.path.halfEdgeIndex === halfEdgeIndex;
      const { leftEdge, rightEdge, leftOffset, rightOffset, leftFace, rightFace } = wall;
      const frontEdge = isLeftWindow ? leftEdge : rightEdge;
      const frontFace = isLeftWindow ? leftFace : rightFace;
      const frontOffset = isLeftWindow ? leftOffset : rightOffset;
      const frontEdgeLen = polyLine2Length(frontEdge);
      const frontHole = getFinalDoorPosition(door, frontEdge, frontEdgeLen, wallMassSystem);

      if (!frontHole) {
        continue;
      }

      // look for extra wall cuts for this door
      tryToAddExtraWallCutFromHole({
        halfEdgeIndex,
        frontHole,
        frontPlane: frontFace.plane,
        frontOffset,
        levels,
        levelId,
        wallMasses,
        allWallCuts,
      });
    }

    // iterate level wall cuts
    if (wallCutTable !== undefined) {
      for (const halfEdgeIndex in wallCutTable) {
        const wallCuts = wallCutTable[halfEdgeIndex];

        if (!wallCuts) {
          continue;
        }

        const wallId = halfEdgeToWall[halfEdgeIndex];

        if (!wallId) {
          continue;
        }

        const wall = walls[wallId];

        if (!wall || wall.wallType !== WallType.SOLID_MASS) {
          continue;
        }

        const isLeftWindow = wall.path.halfEdgeIndex.toString() === halfEdgeIndex;
        const { leftEdge, rightEdge, leftOffset, rightOffset, leftFace, rightFace } = wall;
        const frontEdge = isLeftWindow ? leftEdge : rightEdge;
        const frontFace = isLeftWindow ? leftFace : rightFace;
        const frontOffset = isLeftWindow ? leftOffset : rightOffset;
        const frontEdgeLen = polyLine2Length(frontEdge);

        // iterate all wall cuts from this edge
        for (const wallCut of wallCuts) {
          // add the wall cut to the new list
          addHalfEdgeToWallCutSet(allWallCuts, levelId, halfEdgeIndex, wallCut.item);

          const frontHole = makeWallCutOutline(wallCut.item, frontEdgeLen, frontFace.plane);

          if (!frontHole) {
            continue;
          }

          // look for extra wall cuts on walls from other levels
          tryToAddExtraWallCutFromHole({
            halfEdgeIndex: parseInt(halfEdgeIndex),
            frontHole,
            frontPlane: frontFace.plane,
            frontOffset,
            levels,
            levelId,
            wallMasses,
            allWallCuts,
          });
        }
      }
    }
  }

  return allWallCuts;
}
